import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

export default function Contact() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      toast({
        title: "Message Sent",
        description: "Thank you for reaching out. I'll get back to you shortly.",
        className: "bg-background border-border text-foreground rounded-none shadow-xl",
      });
      // Reset form
      (e.target as HTMLFormElement).reset();
    }, 1000);
  };

  return (
    <div className="flex flex-col pt-10 min-h-screen bg-background">
      
      {/* Page Header */}
      <section className="pt-20 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-foreground leading-[1.1] tracking-tight mb-8">
            Let's build something together.
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed text-balance mx-auto">
            Whether you need strategic data analysis, a conversion-focused website, or a thoughtfully built application — I'd love to hear about your project.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 pb-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
            
            {/* Form Column */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 md:p-12 border border-border/60 shadow-sm rounded-sm">
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Name</Label>
                    <Input 
                      id="name" 
                      required 
                      className="rounded-none border-t-0 border-l-0 border-r-0 border-b-border/60 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-base pb-3 rounded-none shadow-none h-auto transition-colors" 
                      placeholder="Jane Doe" 
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Email</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      required 
                      className="rounded-none border-t-0 border-l-0 border-r-0 border-b-border/60 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-base pb-3 rounded-none shadow-none h-auto transition-colors" 
                      placeholder="jane@example.com" 
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Project Type</Label>
                    <Select required>
                      <SelectTrigger className="rounded-none border-t-0 border-l-0 border-r-0 border-b-border/60 bg-transparent px-0 focus:ring-0 focus:border-primary text-base pb-3 h-auto shadow-none">
                        <SelectValue placeholder="Select an area of focus" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-border shadow-md rounded-none">
                        <SelectItem value="data">Data Analysis</SelectItem>
                        <SelectItem value="design">Website Design</SelectItem>
                        <SelectItem value="app">App Development</SelectItem>
                        <SelectItem value="other">Other / Mixed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 pt-4">
                    <Label htmlFor="message" className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Message</Label>
                    <Textarea 
                      id="message" 
                      required 
                      rows={5}
                      className="rounded-none border-border/60 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base resize-none shadow-none transition-colors" 
                      placeholder="Tell me about what you're working on..." 
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary/90 text-white rounded-none py-7 text-sm uppercase tracking-wider font-semibold transition-all mt-4"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </div>
            </div>

            {/* Info Column */}
            <div className="lg:col-span-5 space-y-16 lg:pt-12">
              <div className="space-y-6">
                <h3 className="text-sm uppercase tracking-widest font-semibold text-primary">Direct Contact</h3>
                <div className="space-y-4 text-lg">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    I typically respond within 1–2 business days. If you're inquiring about a specific project, please include estimated timelines and budgets if known.
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-sm uppercase tracking-widest font-semibold text-primary">Connect</h3>
                <div className="flex flex-col space-y-4 text-lg">
                  <a href="https://www.linkedin.com/in/shateara-davis-90750a89/" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors flex items-center gap-2 group w-fit">
                    LinkedIn
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </a>
                  <a href="https://shatearadavis.medium.com/" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors flex items-center gap-2 group w-fit">
                    Medium
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </a>
                  <a href="https://x.com/ShatearaD94502" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors flex items-center gap-2 group w-fit">
                    X (Twitter)
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </a>
                  <a href="https://www.kaggle.com/shatearad" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors flex items-center gap-2 group w-fit">
                    Kaggle
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </a>
                  <a href="https://www.youtube.com/@ShatearaDavis" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors flex items-center gap-2 group w-fit">
                    YouTube
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </a>
                </div>
              </div>

              <div className="pt-8 border-t border-border/40">
                <div className="bg-plum p-8 text-white rounded-sm text-center space-y-6">
                  <h3 className="font-serif text-2xl">Prefer to schedule directly?</h3>
                  <a href="https://calendly.com/shateara-davis-proton" target="_blank" rel="noopener noreferrer" className="block">
                    <Button className="bg-secondary hover:bg-secondary/90 text-white rounded-none px-8 py-6 text-sm uppercase tracking-wider font-semibold w-full">
                      Book a Call
                    </Button>
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
